import { useEffect, useRef, useState } from 'react'
import { isProduct, type tProductPreview } from 'src/shared/frontendTypes/product'
import type { tItem, tItems } from 'src/shared/frontendTypes/warenkorb'
import type { tItems as WishlistItems } from 'src/shared/frontendTypes/wunschliste'
import { post } from 'src/functions/fetch'
import { getCartPriceAmount } from 'src/functions/cartPriceLock'
import { buildRecommendationCartItem, recommendationPrice } from 'src/pages/Warenkorb/Modern/recommendations'
import config from 'src/config'

export type WishlistCartState = { message?: string; error?: boolean; added?: boolean; updatedItem?: tItem }
export type WishlistCartActions = {
  busy: string | null
  states: Record<string, WishlistCartState>
  add: (product: tProductPreview) => Promise<void>
}

/** Resolve the exact saved variant against current catalog data before adding. */
export default function useWishlistCart(saved: WishlistItems, items: tItems, addItem: (item: tItem) => void): WishlistCartActions {
  const [busy, setBusy] = useState<string | null>(null)
  const [states, setStates] = useState<Record<string, WishlistCartState>>({})
  const current = useRef({ saved, items, states, addItem })
  current.current = { saved, items, states, addItem }
  const request = useRef<AbortController>()
  useEffect(() => () => request.current?.abort(), [])

  const add = async (preview: tProductPreview) => {
    if (request.current && !request.current.signal.aborted) return
    const controller = new AbortController()
    request.current = controller
    const key = preview.key
    setBusy(key)
    const previous = current.current.states[key]
    const update = (state: WishlistCartState) => setStates(value => ({ ...value, [key]: state }))
    try {
      const data = await post({ url: `product/${preview.id}`,
        body: { ...config.shop, skipAlsoPurchased: true, skipCrossSellSections: true, skipBreadcrumb: true }, signal: controller.signal })
      if (controller.signal.aborted || current.current.saved[key]?.id !== preview.id) return
      const item = isProduct(data) && data.id === preview.id
        ? buildRecommendationCartItem(data, key, current.current.items) : null
      if (!item) {
        update({ ...previous, added: false, error: true, message: 'Diese Ausführung ist gerade nicht verfügbar. Bitte öffnen Sie das Produkt für weitere Möglichkeiten.' })
        return
      }
      const shown = previous?.updatedItem
      const shownPrice = getCartPriceAmount(shown?.price ?? recommendationPrice(preview), 1, shown || preview)
      if (Math.abs(getCartPriceAmount(item.price, 1, item) - shownPrice) > .005) {
        update({ updatedItem: item, message: 'Der Preis wurde aktualisiert. Prüfen Sie ihn bitte und klicken Sie erneut auf „In den Warenkorb“.' })
        return
      }
      current.current.addItem(item)
      update({ updatedItem: item, added: true, message: 'Die gewählte Ausführung wurde zum Warenkorb hinzugefügt.' })
    } catch {
      if (!controller.signal.aborted) update({ ...previous, added: false, error: true, message: 'Das Hinzufügen hat gerade nicht geklappt. Bitte versuchen Sie es erneut.' })
    } finally {
      if (!controller.signal.aborted) setBusy(null)
      controller.abort()
    }
  }
  return { busy, states, add }
}
